/**
 * SegmentedControl — pill toggle (V2 design system, port z FarmaPro).
 *
 * Dwa rozmiary:
 *  - fullWidth=true  → hero (LoginPage Logowanie/Rejestracja): grid cols, py-3, text-sm
 *  - fullWidth=false → compact (period filters typu Wpływy/Wydatki/Wszystkie): flex row, py-1.5, text-xs
 *
 * Accessibility: role=tablist/tab, aria-selected, keyboard ←/→.
 */
import React, { useRef } from 'react';

export interface SegmentOption<T extends string = string> {
  key: T;
  label: string;
  icon?: React.ReactNode;
}

interface SegmentedControlProps<T extends string = string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** true = hero size (LoginPage), false = compact (secondary nav) */
  fullWidth?: boolean;
  ariaLabel?: string;
  className?: string;
}

function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  fullWidth = false,
  ariaLabel = 'Wybierz opcję',
  className = '',
}: SegmentedControlProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    const tabs = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    if (!tabs) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      tabs[(idx + 1) % tabs.length]?.focus();
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      tabs[(idx - 1 + tabs.length) % tabs.length]?.focus();
    }
  };

  const containerCls = fullWidth
    ? `grid gap-1 p-1 bg-slate-100 dark:bg-slate-700/50 rounded-2xl ${className}`
    : `flex gap-0.5 p-0.5 bg-slate-100 dark:bg-slate-800 rounded-xl ${className}`;

  const gridStyle = fullWidth
    ? ({ gridTemplateColumns: `repeat(${options.length}, 1fr)` } as React.CSSProperties)
    : undefined;

  const activeCls = fullWidth
    ? 'py-3 text-sm font-bold rounded-xl bg-white dark:bg-slate-800 text-sky-600 dark:text-sky-400 shadow-md shadow-black/5 transition-all'
    : 'px-3 py-1.5 rounded-lg text-xs font-bold bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm transition-all';

  const inactiveCls = fullWidth
    ? 'py-3 text-sm font-semibold rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-all'
    : 'px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-all';

  return (
    <div ref={listRef} role="tablist" aria-label={ariaLabel} className={containerCls} style={gridStyle}>
      {options.map((opt, idx) => (
        <button
          key={opt.key}
          type="button"
          role="tab"
          aria-selected={value === opt.key}
          tabIndex={value === opt.key ? 0 : -1}
          onClick={() => onChange(opt.key)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          className={`flex items-center justify-center gap-1.5 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1 focus-visible:outline-none ${
            value === opt.key ? activeCls : inactiveCls
          }`}
        >
          {opt.icon}
          <span>{opt.label}</span>
        </button>
      ))}
    </div>
  );
}

export default SegmentedControl;
